<!--
  Created 30.10.2023
  Proposal versions component for proposal show page.
-->
<template>
  <SlideOverWithButton
    is-wide
    :order="2"
    button-title="Proposal versions"
    :icon="ArchiveBoxIcon"
    :slide-open="showModal"
    @update:slide-open="(e) => slideOpen(e)">
    <template #title>
      <h3 class="text-xl font-medium leading-6 text-gray-900">
        Proposal versions
      </h3>
    </template>
    <template #content>
      <!-- Proposal versions -->
      <div class="h-auto w-full space-y-4">
        <p>
          Click a version to open in a new tab, or create a new version.
        </p>
        <div
          v-if="!proposal.brand?.terms_media_id"
          class="rounded-md bg-red-50 p-4">
          <div class="flex">
            <div class="shrink-0">
              <ExclamationTriangleIcon
                class="size-5 text-red-400"
                aria-hidden="true" />
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">
                Selected Brand is missing Terms and Conditions
              </h3>
              <div class="mt-2 space-y-1 text-sm text-red-700">
                <p>
                  The currently selected Brand (<span class="font-medium italic">{{ proposal.brand?.name || "TravelOffice" }}</span>) does not include Terms and Conditions,
                  so the client will be unable to accept the proposal version.
                </p>
                <p>To fix this, please go to your team settings and upload your Terms and Conditions onto the selected brand.</p>
                <p>Proposal versions cannot be created unless this is in place.</p>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-2 flex flex-col">
          <!-- list versions -->
          <div class="w-full rounded rounded-b-none border border-gray-300 bg-gray-100 shadow">
            <div
              v-if="versions.length === 0"
              class="m-2">
              <spinning-circle
                v-if="loading"
                class="w-10" />
              <i v-if="loading">Loading versions</i>
              <i v-else>No versions created</i>
            </div>
            <div
              v-else
              class="m-2 min-h-14 overflow-scroll bg-white">
              <ul
                role="list"
                class="divide-y divide-gray-200">
                <li v-if="adding">
                  <div class="block hover:bg-gray-50">
                    <div class="flex items-center p-2 sm:px-3">
                      <div class="flex min-w-0 flex-1 items-center">
                        <div class="shrink-0">
                          <img
                            class="size-8 rounded-full object-cover "
                            :src="$page.props.user.profile_photo_url"
                            alt="">
                        </div>
                        <div class="flex flex-col px-4">
                          <div class="h-4 w-32 bg-gray-100" />
                          <div class="mt-1 h-4 w-16 bg-gray-100" />
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
                <li v-if="duplicate">
                  <div class="block hover:bg-gray-50">
                    <div class="flex items-center p-2 sm:px-3">
                      <div class="flex min-w-0 flex-1 items-center">
                        <div class="shrink-0">
                          <img
                            class="size-8 rounded-full object-cover "
                            :src="$page.props.user.profile_photo_url"
                            alt="">
                        </div>
                        <div class="w-full min-w-0 flex-1 px-4">
                          <div class="truncate text-sm font-medium text-red-700">
                            Duplicate error - a new version was not added
                          </div>
                          <div class="space-x-1 text-sm text-gray-500">
                            There is no changes from latest existing version
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
                <li
                  v-for="version in versions.slice().reverse()"
                  :key="version.id">
                  <div
                    class="block cursor-pointer hover:bg-yellow-200"
                    @click="openLink(version.url)">
                    <div class="flex items-center p-2 sm:px-3">
                      <div class="flex min-w-0 flex-1 items-center">
                        <div class="shrink-0">
                          <img
                            class="size-8 rounded-full object-cover "
                            :src="version.creator.profile_photo_url"
                            alt="">
                        </div>
                        <div
                          v-if="updating === version.id"
                          class="flex flex-col px-4">
                          <div class="h-4 w-32 bg-gray-100" />
                          <div class="mt-1 h-4 w-16 bg-gray-100" />
                        </div>
                        <div
                          v-else-if="noUpdates === version.id"
                          class="w-full min-w-0 flex-1 px-4">
                          <div class="truncate text-sm font-medium text-red-700">
                            Update error - this version was not updated
                          </div>
                          <div class="space-x-1 text-sm text-gray-500">
                            There is no changes from the existing version
                          </div>
                        </div>
                        <div
                          v-else
                          class="flex w-full min-w-0 flex-1 flex-row justify-between px-4">
                          <div>
                            <p class="truncate text-sm font-medium text-indigo-600">
                              Proposal version {{ version.proposalVersionNumber }}<span class="text-gray-500"> - id{{ version.proposalVersionId }}</span>
                              <template v-if="version.accepted">
                                &bull;&nbsp;<span class="text-sm font-medium text-green-600">Accepted</span>
                              </template>
                            </p>
                            <p
                              class="flex items-center space-x-1 text-sm text-gray-500"
                              :title="'Created: ' + formatDateTimeToLocaleCustomString(version.created_at, 'en-GB')">
                              <span>By {{ version.creator.name }},</span>
                              <human-readable-time
                                :date="version.created_at"
                                prefix="" />
                            </p>
                            <p
                              v-if="version.accepted && version.updated_at !== version.created_at"
                              class="flex items-center space-x-1 text-sm text-gray-500"
                              :title="'Updated: ' + formatDateTimeToLocaleCustomString(version.updated_at, 'en-GB')">
                              <span>Last updated:</span>
                              <human-readable-time
                                :date="version.updated_at"
                                prefix="" />
                            </p>
                          </div>
                          <div
                            v-if="version.accepted"
                            class="flex items-center">
                            <button
                              class="rounded border-2 border-cyan-800 bg-cyan-700
                                px-3 py-1.5 text-sm font-medium text-white opacity-90
                                hover:border-orange-400 hover:bg-orange-300 hover:text-orange-600 hover:opacity-100"
                              @click.stop="updateProposalVersion(version)">
                              Update this version
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <!-- create new version -->
          <button
            class="rounded rounded-t-none bg-cyan-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-cyan-800"
            :class="!proposal.brand?.terms_media_id ? 'cursor-not-allowed bg-gray-400 hover:bg-gray-400' : 'cursor-pointer'"
            :disabled="!proposal.brand?.terms_media_id"
            @click="createProposalVersion(proposal.brand?.terms_media_id)">
            Create new version
          </button>
        </div>
        <div>
          <button
            class="mt-4 rounded border border-gray-300 bg-gray-100 px-3 py-1.5
              text-sm font-medium text-gray-700 hover:border-gray-400 hover:bg-gray-300"
            @click="showModal=false">
            Close panel
          </button>
        </div>
      </div>
    </template>
  </SlideOverWithButton>
</template>
<script setup>
/* external */
import { ArchiveBoxIcon } from '@heroicons/vue/24/outline';
import { ExclamationTriangleIcon } from '@heroicons/vue/20/solid';
/* local */
import SlideOverWithButton from '@/Pages/Proposal/Components/SlideOverWithButton.vue';
import HumanReadableTime from '@/ComponentsDeprecated/Time/HumanReadableTime.vue';
import SpinningCircle from '@/ComponentsDeprecated/Loaders/SpinningCircle.vue';
import {
  computed, ref,
} from 'vue';
import {
  currentDateLocalized, formatDateTimeToLocaleCustomString,
} from '@/utils';
import { notify } from 'notiwind';

/* *********************************************************************
 * Page declarations
 * ****************************************************************** */

const props = defineProps({
  proposal: {
    type: Object,
    required: true,
  },
});

const showModal = ref(false);
const loading = ref(true);

const versions = ref([]);

const adding = ref(false);
const duplicate = ref(false);
const updating = ref(false);
const noUpdates = ref(false);

/**
 *
 * @param {boolean} state - state to set slideOpen to
 */
function slideOpen(state) {
  showModal.value = state;
  if (state) {
    axios.get(route('api.proposal.version.index', props.proposal.id))
      .then((response) => {
        versions.value = response.data.map((version, index) => ({
          proposalVersionId: version.id,
          proposalVersionNumber: index + 1,
          ...version,
        }));
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        loading.value = false;
      });
  }
}

/**
 * Open link in new tab
 * @param {string} url - url to open in new tab
 */
function openLink(url) {
  window.open(url, '_blank');
}

/**
 * Create new proposal version
 * @param {boolean} creatable - if proposal is creatable
 */
function createProposalVersion(creatable = false) {
  if (creatable) {
    adding.value = true;
    duplicate.value = false;
    axios.post(route('api.proposal.version.store', props.proposal.id))
      .then((response) => {
        if (!response.data.duplicate) {
          const versionsCount = versions.value.length;
          const newVersion = {
            ...response.data.version,
            proposalVersionId: response.data.version.id,
            proposalVersionNumber: versionsCount + 1,
          };
          versions.value.push(newVersion);
        } else {
          duplicate.value = true;
          setTimeout(() => {
            duplicate.value = false;
          }, 4000);
        }
      })
      .catch((error) => {
        if (error.response.status === 422 && error.response.data.missing_brand) {
          notify({
            type: 'danger',
            title: 'Unable to create version',
            text: 'There was an issue with reading the Brand set in the proposal. Please go to proposal settings and make any change to your brand settings.',
          }, 8000);
        } else {
          console.log(error);
        }
      })
      .finally(() => {
        adding.value = false;
      });
  }
}

/**
 *
 * @param version
 */
function updateProposalVersion(version) {
  updating.value = version.id;
  noUpdates.value = false;
  axios.put(route('api.proposal.version.update', {
    proposal: props.proposal.id, version: version.id,
  }))
    .then((response) => {
      if (!response.data.duplicate) {
        const versionIndex = versions.value.findIndex((v) => v.id === version.id);
        versions.value[versionIndex] = {
          ...versions.value[versionIndex],
          ...response.data.version,
        };
      } else {
        noUpdates.value = version.id;
        setTimeout(() => {
          noUpdates.value = false;
        }, 4000);
      }
    })
    .catch((error) => {
      console.log(error);
    })
    .finally(() => {
      updating.value = false;
    });
}

</script>
